"use client";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { FaFileDownload } from "react-icons/fa";
import { InView } from "react-intersection-observer";

import { FileFrame, DeleteTooltip, PdfView } from "components";
import { DELETE_DOCUMENT_CONFIRM, EXCEPTION } from "constants/modalTemplate.tsx";
import { ApplicationMessage, GetMessagesQuery, SenderType } from "graphql/type.ts";
import useDeleteMessage from "hooks/api/chat/useDeleteMessage.tsx";
import useReadMessage from "hooks/api/chat/useReadMessage";
import { useModal } from "hooks/useModal.tsx";
import { InfiniteKeyedMutator } from "types/InfiniteKeyedMutator";
import { onDownloadByFileUrl } from "utils/download.util";

const SYSTEM_NAME = "SYNC PORT";

const MessageBubble = ({
  message: { id, content, sendAt, sender, file, isRead, bulkMessageId },
  mutate,
  applicationId,
}: {
  message: ApplicationMessage;
  mutate: InfiniteKeyedMutator<GetMessagesQuery[]>;
  applicationId: string;
}) => {
  const { deleteMessage } = useDeleteMessage();
  const { openNotificationModal, openErrorModal, closeModal } = useModal();
  const { readMessage } = useReadMessage();

  const [fileUrl, setFileUrl] = useState(file?.url);

  const onClickDeleteButton = async () => {
    const res = await deleteMessage({ messageId: id })
      .catch(() => openErrorModal(EXCEPTION))
      .catch((e) => {
        throw new Error(e);
      });
    if (res) {
      mutate();
      closeModal();
    }
  };
  const onOpenDeleteMessageConfirmModal = () => {
    openNotificationModal(DELETE_DOCUMENT_CONFIRM(() => onClickDeleteButton()));
  };

  useEffect(() => {
    if (fileUrl === undefined) {
      setFileUrl(file?.url);
    }
  }, [file?.url, fileUrl]);

  // 貸主であるか
  const isLessor = sender.type === SenderType.Lessor;
  const isSystem = sender.type === SenderType.System;
  const isPdf = file?.fileType.toUpperCase() === "PDF";
  const filename = file?.originUrl?.split("/").pop() ?? "";

  const contentLines = content.split("\n");

  const onClickDownload = async () => {
    if (!fileUrl) return;
    await onDownloadByFileUrl({
      filename: filename,
      fileUrl: fileUrl,
      isPdf: isPdf,
    });
  };

  const handleChange = (inView: boolean) => {
    if (inView && !isRead && !isLessor) {
      readMessage({ applicationId });
    }
  };

  return (
    <InView
      className={clsx("flex gap-2.5 pt-1", isLessor ? "flex-row-reverse" : "flex-row")}
      as="div"
      onChange={handleChange}
    >
      <img
        src={isLessor ? "/picture/lessor-icon.png" : isSystem ? "/picture/system-icon.png" : "/picture/lessee-icon.png"}
        alt={sender.name}
        className={clsx(!isSystem && "border p-[10px]", "border-button-disabled h-[44px] w-[44px] rounded-full")}
      />
      <div className={clsx("flex flex-col text-gray", isLessor ? "items-end" : "items-start")}>
        <span className="text-xs font-bold">{isLessor ? "あなた" : isSystem ? SYSTEM_NAME : sender.name}</span>
        <div className={clsx("flex gap-2", isLessor ? "flex-row-reverse" : "flex-row")}>
          <div
            className={clsx(
              "flex max-w-[500px] justify-between rounded py-4 pl-6 pr-4 text-sm text-black",
              isLessor ? "bg-[#f6f6f6]" : isSystem ? "bg-link-light" : "bg-attention-light"
            )}
          >
            <div className="flex w-full flex-col">
              {isLessor && bulkMessageId && (
                <div className="mb-2 flex h-6 items-center justify-center pt-1 pr-1 pb-0.5 pl-1 rounded-sm bg-gray">
                  <span className="text-xs font-bold leading-[1.5] text-white">一括配信</span>
                </div>
              )}
              <div>
                {contentLines.map((c, index) => (
                  <p key={index}>
                    {c}
                    {contentLines.length > index && <br />}
                  </p>
                ))}
              </div>
              {file && (
                <div className="mt-2 flex items-center gap-4 rounded bg-white p-4">
                  <div className="border border-gray">
                    {!!file && isPdf && <PdfView src={fileUrl} size={80} />}
                    {!!file && !isPdf && (
                      <FileFrame src={fileUrl} size={84} isHidePlusButton={true} wrapperClassName="!py-0" />
                    )}
                  </div>
                  <div className="flex flex-1 flex-col gap-2">
                    <span className="text-sm">{filename}</span>
                    {!isLessor && (
                      <div className="flex cursor-pointer gap-1">
                        <FaFileDownload size={14} />
                        <span className="text-xs text-primary">
                          <button onClick={onClickDownload}>ダウンロード</button>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {isLessor && <DeleteTooltip onRemove={onOpenDeleteMessageConfirmModal} />}
          </div>
          <div className="flex flex-col items-center">
            <span className="text-xs text-gray">{sendAt}</span>
            {isRead && isLessor && <span className="text-xs font-bold">既読</span>}
          </div>
        </div>
      </div>
    </InView>
  );
};

export default MessageBubble;
